import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, MapPin, User, FileText, Edit, X } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ShareAppointment } from "./ShareAppointment";

interface AppointmentDetailViewProps {
  appointment: any;
  onEdit: () => void;
  onClose: () => void;
}

export const AppointmentDetailView: React.FC<AppointmentDetailViewProps> = ({
  appointment,
  onEdit,
  onClose
}) => {
  const appointmentDate = new Date(appointment.date);
  const endDate = appointment.end_date ? new Date(appointment.end_date) : null;
  const hasTime = appointmentDate.getHours() !== 0 || appointmentDate.getMinutes() !== 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold mb-2">{appointment.title}</h3>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  appointment.type === 'Audiência' 
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : appointment.type === 'Prazo'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                      : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                }`}>
                  {appointment.type}
                </span>
              </div>
              <ShareAppointment appointment={appointment} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium">Data</p>
                  <p className="text-sm text-gray-600">
                    {format(appointmentDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </p>
                </div>
              </div>

              {hasTime && (
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium">Horário</p>
                    <p className="text-sm text-gray-600">
                      {format(appointmentDate, 'HH:mm')}
                      {endDate && ` - ${format(endDate, 'HH:mm')}`}
                    </p>
                  </div>
                </div>
              )}

              {appointment.clients?.name && (
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium">Cliente</p>
                    <p className="text-sm text-gray-600">{appointment.clients.name}</p>
                  </div>
                </div>
              )}

              {appointment.location && (
                <div className="flex items-center space-x-3">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium">Local</p>
                    <p className="text-sm text-gray-600">{appointment.location}</p>
                  </div>
                </div>
              )}
            </div>

            {appointment.notes && (
              <div className="flex items-start space-x-3">
                <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Observações</p>
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">
                    {appointment.notes}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={onClose}>
          <X className="mr-2 h-4 w-4" />
          Fechar
        </Button>
        {!appointment.archived && (
          <Button 
            onClick={onEdit}
            className="bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]"
          >
            <Edit className="mr-2 h-4 w-4" />
            Editar
          </Button>
        )}
      </div>
    </div>
  );
};
